import { useEffect, useState } from "react";
import { Bell, Headphones, Menu, Search, UserRound, X } from "lucide-react";
import AiOrderAssistant from "./AiOrderAssistant";
import { useAuth } from "../auth/AuthGate";
import { useLocation, useNavigate } from "react-router-dom";

const NAV = [
  { label: "Home", href: "/#top" },
  { label: "Services", href: "/#services" },
  { label: "How It Works", href: "/#process" },
  { label: "Trending", href: "/#gallery" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

const supportLauncherEnabled =
  process.env.REACT_APP_ENABLE_CUSTOMER_SUPPORT_LAUNCHER !== "false";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(() => new URLSearchParams(window.location.search).get("assistant") === "1");
  const { user, openAuth } = useAuth();
  useEffect(() => {
    if (new URLSearchParams(location.search).get("assistant") === "1") setAiOpen(true);
  }, [location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#FFF8F2]/90 backdrop-blur-md border-b border-[#F4E9DB] shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-12 xl:px-16 flex items-center gap-5 h-20">
        <a href="/#top" data-testid="nav-logo" className="shrink-0 flex flex-col items-start justify-center leading-none min-w-[190px]">
          <span className="font-display text-2xl md:text-3xl tracking-tight">
            Stich<span className="text-[#F46524]">B</span>
          </span>
          <span className="font-label text-[#8a8781] hidden sm:inline mt-1 text-[9px] md:text-[10px] tracking-[0.2em]">Your Plan B Tailor</span>
        </a>

        <nav className="hidden xl:flex flex-1 items-center justify-center gap-5 2xl:gap-7 min-w-0">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#2B2B2B] link-underline"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto shrink-0 flex items-center gap-2 md:gap-3">
          <button data-public-action="true" onClick={() => navigate("/search")} className="h-11 w-11 rounded-xl border border-[#E9D7C8] bg-white flex items-center justify-center shadow-sm" aria-label="Search StitchB"><Search size={18}/></button>
          <button data-public-action="true" onClick={() => user ? navigate("/notifications") : openAuth()} className="h-11 w-11 rounded-xl border border-[#E9D7C8] bg-white flex items-center justify-center shadow-sm" aria-label="Notifications"><Bell size={18}/></button>
          {supportLauncherEnabled ? <button data-public-action="true" onClick={() => user ? navigate("/profile/support", { state: { startSupport: true } }) : openAuth()} className="h-11 w-11 rounded-xl border border-[#E9D7C8] bg-white flex items-center justify-center shadow-sm" aria-label="Customer support" title="Customer support"><Headphones size={18}/></button> : null}
          <button
            data-public-action="true"
            onClick={() => user ? setAiOpen(true) : openAuth()}
            data-testid="nav-book-button"
            className="btn-primary hidden sm:inline-flex text-sm whitespace-nowrap px-4 lg:px-5"
          >
            AI Help to Place Order
          </button>
          <div className="relative">
            <button data-public-action="true" onClick={() => user ? navigate("/profile") : openAuth()} className="h-11 rounded-xl border border-[#E9D7C8] bg-white px-2.5 sm:px-3 flex items-center gap-2 shadow-sm whitespace-nowrap" aria-label={user?"Open customer profile":"Login or sign up"}>
              {user?.photoURL?<img src={user.photoURL} alt="Customer" className="w-7 h-7 rounded-full object-cover"/>:<span className="w-7 h-7 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center"><UserRound size={16}/></span>}
              <span className="hidden lg:block text-left leading-tight"><span className="block text-[10px] text-[#8a8781]">{user?"Hi":"Customer"}</span><strong className="block text-xs max-w-[130px] truncate">{user?(user.displayName||user.email?.split("@")[0]||"Customer"):"Login / Sign up"}</strong></span>
            </button>
          </div>
          <button
            data-public-action="true"
            data-testid="nav-menu-toggle"
            className="xl:hidden text-[#2B2B2B] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-[#FFF8F2] border-t border-[#F4E9DB] px-6 py-6" data-testid="nav-mobile-panel">
          <div className="flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium"
              >
                {n.label}
              </a>
            ))}
            <button
              data-public-action="true"
              onClick={() => { setOpen(false); user ? setAiOpen(true) : openAuth(); }}
              className="btn-primary text-sm w-full justify-center"
              data-testid="nav-mobile-book"
            >
              AI Help to Place Order
            </button>
            {supportLauncherEnabled ? <button
              data-public-action="true"
              onClick={() => { setOpen(false); user ? navigate("/profile/support", { state: { startSupport: true } }) : openAuth(); }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#E9D7C8] bg-white px-4 py-3 font-semibold"
            >
              <Headphones size={18}/> Customer support
            </button> : null}
          </div>
        </div>
      )}
      <AiOrderAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </header>
  );
}
